import { Block, Button, Icon, TabbedSection, Text, useTelemetry } from '@snowflake/core-ui';
import { SignupEventType } from '@signup/shared';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckmarkIcon, CopyIcon, DesktopMonitorIcon, TerminalIcon } from './terminalIcons';
import {
  CLI_COMMAND,
  CLI_SUBTITLE,
  DESKTOP_DOWNLOAD_URLS,
  PLATFORM_OPTIONS,
  type TabKey,
} from './cortexInstallBlockConstants';
import {
  cardRowStyle,
  cardStyle,
  cardSubtitleStyle,
  cardTitleStyle,
  platformSelectorWrapperStyle,
  PRIMARY_TEXT,
} from './cortexInstallBlockStyles';
import { InlinedSelector } from '../Cards/shared/InlinedSelector';
import { useWindowsOrNot } from '../../utils/useWindowsOrNot';
import { SignupEventName } from '../../utils/SignupLogger';

interface InstallBlockBodyProps {
  tab: TabKey;
}

const InstallBlockBody: React.FC<InstallBlockBodyProps> = ({ tab }) => {
  const { logEvent } = useTelemetry();
  const { formatMessage } = useIntl();
  const [platformKey, setPlatformKey] = React.useState<string>(PLATFORM_OPTIONS[tab][0].value);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    setPlatformKey(PLATFORM_OPTIONS[tab][0].value);
    setCopied(false);
  }, [tab]);

  const platformLabel = PLATFORM_OPTIONS[tab].find(opt => opt.value === platformKey)?.label ?? '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CLI_COMMAND[tab]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      logEvent({
        event: SignupEventName.CORTEX_CODE_COPY_CLI_COMMAND,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: { tab },
      });
    } catch (err) {
      logEvent({
        event: SignupEventName.CORTEX_CODE_COPY_CLI_COMMAND_ERROR,
        type: SignupEventType.UI_ERROR,
        data: { tab, error: err instanceof Error ? err.message : String(err) },
      });
    }
  };

  const handleDownload = () => {
    const url = DESKTOP_DOWNLOAD_URLS[platformKey] ?? '#';
    logEvent({
      event: SignupEventName.CORTEX_CODE_DOWNLOAD_DESKTOP,
      type: SignupEventType.UI_RESPONSE_SUCCESS,
      data: { platform: platformKey },
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Block display="flex" style={cardRowStyle}>
      <div style={cardStyle}>
        <Block display="flex" justifyContent="space-between" alignItems="center">
          <TerminalIcon />
          <Button
            use="text"
            size="small"
            onClick={handleCopy}
            aria-label={formatMessage({ id: 'Copy install command' })}
          >
            {copied ? (
              <CheckmarkIcon strokeWidth={1} />
            ) : (
              <CopyIcon color={PRIMARY_TEXT} strokeWidth={1} />
            )}
          </Button>
        </Block>
        <Text weight="semi-bold" style={cardTitleStyle}>
          <FormattedMessage id="Install CLI" />
        </Text>
        <Text style={cardSubtitleStyle}>{CLI_SUBTITLE[tab]}</Text>
      </div>
      <div style={cardStyle}>
        <Block display="flex" justifyContent="space-between" alignItems="center">
          <DesktopMonitorIcon />
          <Button
            use="text"
            size="small"
            onClick={handleDownload}
            aria-label={formatMessage({ id: 'Download desktop app' })}
          >
            <Icon icon="download-light" size={16} />
          </Button>
        </Block>
        <Text weight="semi-bold" style={cardTitleStyle}>
          <FormattedMessage id="Download Desktop" />
        </Text>
        <div style={platformSelectorWrapperStyle}>
          <InlinedSelector
            options={PLATFORM_OPTIONS[tab]}
            value={platformKey}
            selectValue={setPlatformKey}
            label={platformLabel}
            fullWidth
          />
        </div>
      </div>
    </Block>
  );
};

export const CortexInstallBlockV2: React.FC = () => {
  const { formatMessage } = useIntl();
  const platform = useWindowsOrNot();
  const [activeTab, setActiveTab] = React.useState<TabKey>(
    platform === 'windows' ? 'windows' : 'mac',
  );

  return (
    <Block className="cortex-install-tabs">
      <TabbedSection
        selected={activeTab}
        onTab={id => setActiveTab(id as TabKey)}
        tabs={[
          {
            id: 'mac',
            label: formatMessage({ id: 'Mac / Linux' }),
            content: <InstallBlockBody tab="mac" />,
          },
          {
            id: 'windows',
            label: formatMessage({ id: 'Windows' }),
            content: <InstallBlockBody tab="windows" />,
          },
        ]}
      />
    </Block>
  );
};
